import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAmplitude } from "../../context/AmplitudeContext";
import { useUID } from "../../context/UIDContext";
import "./styles.css";
import { useConfig } from "../../context/ConfigContext";
import {
  QuestionMethodMapping,
  QuestionSolutionMapping,
} from "../../data/QuestionMapping";
import {
  AiResultENEmailMappings,
  AiResultDEEmailMappings,
} from "../../data/AiResultEmailMapping";
import { Email } from "../Email/Email";
import { LandingPage } from "./LandingPage";
import { ProductRecoPage } from "./ProductRecoPage";
import {
  ProductDataMethodMapping,
  ProductDataSolutionMapping,
} from "../../data/ProductData";
import {
  ProductDataMethodENMapping,
  ProductDataSolutionENMapping,
} from "../../data/ProductDataEn";
import { ProductInfoPage } from "./ProductInfoPage/ProductInfoPage";
import { ProductCheckoutPage } from "./ProductCheckoutPage/ProductCheckoutPage";

const DefaultRecommendation = {
  method: "Sensitive (Sensitive)",
  solution: "Structure Repair",
};

const buildRecommendation = (aiResult, selectedAnswers) => {
  let method = DefaultRecommendation.method;

  /* If user answered */
  if (
    selectedAnswers["question-1"] !== "" &&
    selectedAnswers["question-1"] !== "question-1-option-5"
  ) {
    method = QuestionMethodMapping[selectedAnswers["question-1"]];
  } else {
    if (aiResult["Hair Volume"].value === "low") {
      method = "Regenerante (Hairloss Control)";
    } else if (aiResult["Hair Gloss"].percentages.Glossy >= 50) {
      method = "Clarifiante";
    } else if (aiResult["Hair Gloss"].percentages.Glossy < 50) {
      method = "Hydratante";
    }
  }

  let solution = DefaultRecommendation.solution;

  /* If user answered */
  if (
    selectedAnswers["question-2"] !== "" &&
    selectedAnswers["question-2"] !== "question-2-option-6"
  ) {
    solution = QuestionSolutionMapping[selectedAnswers["question-2"]];
  } else {
    if (
      aiResult["Hair Length"].value === "long (female)" ||
      aiResult["Hair Length"].value === "long (male)"
    ) {
      solution = "Long Hair";
    } else if (aiResult["Hair Gloss"].percentages.Glossy < 50) {
      solution = "Structure Repair";
    } else if (
      aiResult["Hair Texture v2"].value === "wavy" ||
      aiResult["Hair Texture v2"].value === "curly" ||
      aiResult["Hair Texture v2"].value === "coily"
    ) {
      solution = "Curl";
    } else if (aiResult["Hair Volume"].value === "low") {
      solution = "Volume";
    }
  }

  return { method: method, solution: solution };
};

const buildAiEmailMappings = (aiResults, lng) => {
  const result = {};

  let AiResultEmailMappings = AiResultDEEmailMappings;
  if (lng !== "de") {
    AiResultEmailMappings = AiResultENEmailMappings;
  }

  for (const [key, value] of Object.entries(aiResults)) {
    const keyMap = AiResultEmailMappings[key].key;
    const valueMap = AiResultEmailMappings[key].value;

    if (key === "Hair Gloss") {
      const glossResult =
        aiResults[key].percentages.Glossy >= 50 ? "Glossy" : "Dry";
      result[keyMap] = valueMap[glossResult];
    } else {
      result[keyMap] = valueMap[value.value];
    }
  }

  return result;
};

export const ProductModule = ({
  apiToken,
  aiResults,
  showEmailPage,
  onBackClick,
  onExitClick,
}) => {
  const { i18n } = useTranslation();
  const trackEvent = useAmplitude();
  const { uid } = useUID();
  const { selectedAnswers, sendEmail } = useConfig();
  const [step, setStep] = useState(showEmailPage ? 0 : 1);
  const [selectedInfoProduct, setSelectedInfoProduct] = useState([]);
  const [selectedFavoriteProducts, setSelectedFavoriteProducts] = useState([]);

  const recommendedData = buildRecommendation(aiResults, selectedAnswers);
  const methodData =
    i18n.language === "de"
      ? ProductDataMethodMapping[recommendedData.method]
      : ProductDataMethodENMapping[recommendedData.method];
  const solutionData =
    i18n.language === "de"
      ? ProductDataSolutionMapping[recommendedData.solution]
      : ProductDataSolutionENMapping[recommendedData.solution];

  const onSendEmail = (trackEmailState = false) => {
    trackEvent("webapp_user_email_sent_pressed", uid);
    const mappedAiResults = buildAiEmailMappings(aiResults, i18n.language);
    sendEmail(
      i18n.language,
      apiToken,
      mappedAiResults,
      {
        headSkinMethodUrl: methodData.Product.value,
        headSkinTreatmentUrl: methodData.Treatment.value,
        hairCareSolutionUrl: solutionData.Product.value,
        hairCareTreatmentUrl: solutionData.Treatment.value,
      },
      trackEmailState,
    );
  };

  const toggleItems = (list) => {
    setSelectedFavoriteProducts((prev) => {
      const prevMap = new Map(prev.map((item) => [item.value, item]));
      const anyExist = list.some((item) => prevMap.has(item.value));
      if (anyExist) {
        list.forEach((item) => prevMap.delete(item.value));
      } else {
        list.forEach((item) => prevMap.set(item.value, item));
      }
      return Array.from(prevMap.values());
    });
  };

  const toggleItem = (item) => {
    setSelectedFavoriteProducts((prev) => {
      const exists = prev.some((i) => i["value"] === item["value"]);

      if (exists) {
        return prev.filter((i) => i["value"] !== item["value"]);
      }

      trackEvent("webapp_user_product_liked", uid, "", "", item?.title);

      return [...prev, item];
    });
  };

  return (
    <>
      {step === 0 && (
        <Email
          onBackClick={onBackClick}
          onSendEmail={() => {
            trackEvent("webapp_user_press_email_page_continue", uid);
            onSendEmail(false);
            setStep(1);
          }}
          onSkip={() => {
            trackEvent("webapp_user_press_email_page_skip", uid);
            setStep(1);
          }}
        />
      )}
      {step === 1 && (
        <LandingPage
          onBackClick={() => {
            showEmailPage ? setStep(0) : onBackClick();
          }}
          onContinue={() => setStep(2)}
        />
      )}
      {step === 2 &&
        (selectedInfoProduct.length === 0 ? (
          <ProductRecoPage
            onBackClick={() => setStep(1)}
            onSendEmail={onSendEmail}
            recommendedData={recommendedData}
            onInfoClick={(productDataItems) => {
              setSelectedInfoProduct(productDataItems);
            }}
            selectedFavorites={selectedFavoriteProducts}
            onFavoriteClick={(productItems) => toggleItems(productItems)}
            onCheckout={() => setStep(3)}
          />
        ) : (
          <ProductInfoPage
            onBackClick={() => setSelectedInfoProduct([])}
            productData={selectedInfoProduct}
            selectedFavorites={selectedFavoriteProducts}
            onFavoriteClick={(productItem) => toggleItem(productItem)}
          />
        ))}
      {step === 3 && (
        <ProductCheckoutPage
          onBackClick={() => setStep(2)}
          selectedFavorite={selectedFavoriteProducts}
          onRemoveFavorite={(productItem) => toggleItem(productItem)}
          onExitClick={onExitClick}
        />
      )}
    </>
  );
};
